import 'twin.macro';
import 'styled-components/macro';
import { FieldInputProps, FieldMetaProps } from 'formik';
import { ReactNode } from 'react';

import { Form } from 'component-lib/atoms/controls/Form';
import { Select, SelectProps } from 'component-lib/atoms/select/Select';

export interface FormSelectProps<T extends string = string>
  extends Omit<SelectProps<T>, 'onOpenChange' | 'onValueChange' | 'onBlur'>,
    Partial<FieldMetaProps<T>>,
    Partial<Omit<FieldInputProps<T>, 'onChange'>> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  touched?: boolean;

  // Faking a ChangeEvent for formik compatibility
  onChange?(event: { target: { name: string; value: T } }): void;
}

export const FormSelect = <T extends string = string>({
  label,
  hint,
  className,
  children,
  name = '',
  onChange,
  onBlur,
  error,
  touched,

  // Unused
  initialTouched,
  initialError,
  initialValue,
  multiple,
  checked,

  // Forwarded
  ...rest
}: FormSelectProps<T>) => {
  const hasError = Boolean(touched && error);
  const hintText = hasError && error ? error : hint;

  const control = (
    <Select<T>
      tw="w-full"
      dropdownEqualWidth
      hasError={hasError}
      name={name}
      onValueChange={value => onChange?.({ target: { name, value } })}
      onBlur={e => {
        e.target.name = name;
        onBlur?.(e);
      }}
      {...rest}
    >
      {children}
    </Select>
  );

  return (
    <Form.Group className={className}>
      {label ? (
        <Form.Label>
          {label}
          {control}
        </Form.Label>
      ) : (
        control
      )}
      {hintText && <Form.Hint error={hasError}>{hintText}</Form.Hint>}
    </Form.Group>
  );
};
